import React, { useState, useMemo, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Small client-side pagination hook + companion `<Paginator>` control bar.
 *
 * Usage:
 *   const { page, pageItems, controls } = usePagination(items, 20);
 *   return (<>{pageItems.map(...)} {controls}</>);
 */
export function usePagination(items = [], initialPageSize = 20) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // Reset to first page whenever the underlying list changes size or filter shrinks past current page
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
    if (page > totalPages) setPage(1);
  }, [items.length, pageSize, page]);

  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = (page - 1) * pageSize;
  const pageItems = useMemo(() => items.slice(start, start + pageSize), [items, start, pageSize]);

  const controls = total > pageSize ? (
    <Paginator
      page={page}
      totalPages={totalPages}
      pageSize={pageSize}
      total={total}
      onPage={setPage}
      onPageSize={n => { setPageSize(n); setPage(1); }}
    />
  ) : null;

  return { page, pageSize, pageItems, controls, total, totalPages };
}

export default function Paginator({ page, totalPages, pageSize, total, onPage, onPageSize }) {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return (
    <div className="flex items-center justify-between gap-3 mt-3 flex-wrap text-xs" data-testid="paginator">
      <div className="text-[#75706A]">
        Showing <span className="mono">{from}</span>–<span className="mono">{to}</span> of <span className="mono font-bold">{total}</span>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-[#75706A]">Per page</label>
        <select
          className="input-field h-8 py-0.5 px-2 text-xs w-20"
          value={pageSize}
          onChange={e => onPageSize(Number(e.target.value))}
          data-testid="page-size"
        >
          {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <div className="flex items-center gap-1">
          <button
            className="btn-ghost px-2 py-1 disabled:opacity-40"
            onClick={() => onPage(page - 1)}
            disabled={page <= 1}
            data-testid="page-prev"
            aria-label="Previous page"
          ><ChevronLeft size={14}/></button>
          <span className="mono px-2">{page} / {totalPages}</span>
          <button
            className="btn-ghost px-2 py-1 disabled:opacity-40"
            onClick={() => onPage(page + 1)}
            disabled={page >= totalPages}
            data-testid="page-next"
            aria-label="Next page"
          ><ChevronRight size={14}/></button>
        </div>
      </div>
    </div>
  );
}
